import React from 'react';
import {Button, Drawer, Form, Input, InputNumber, Row, Tooltip} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import _ from 'lodash';

import {useAddLocationMutation, useUpdateLocationMutation} from '../../../store/api/locationsApi';
import {roundNumberUpToPrecision} from '../../../helpers/misc';
import {messageType, showMessages} from '../../../helpers/message';
import {nothingToUpdate} from '../../../helpers/text';
import {LocationDrawerProps, LocationFinishValues} from '../../../types/locationTypes';
import {BaseError} from '../../../types/apiTypes';

import './LocationsView.less';

const LocationManagementDrawer = ({action, drawerOpen, currentLocation, handleClose}: LocationDrawerProps) => {
    const [form] = Form.useForm();

    const lat = Form.useWatch('latitude', form);
    const long = Form.useWatch('longitude', form);
    const latLongEmpty = lat == null && long == null; // null/undefined, but not 0.

    const mobileMode = window.innerWidth <= 768;
    const drawerWidth = mobileMode ? "100%" : "45%";

    const [createLocation, {isLoading: createLoading, isSuccess: createSuccess, isError: isCreateError, error: createError, reset: createReset }] = useAddLocationMutation();
    const [updateLocation, {isLoading: updateLoading, isSuccess: updateSuccess, isError: isUpdateError, error: updateError, reset: updateReset}] = useUpdateLocationMutation();

    const handleFinish = (values: LocationFinishValues) => {
        if (!latLongEmpty) {
            // Round lat/long to nearest 6 decimals.
            values['latitude'] = roundNumberUpToPrecision(values['latitude'], 6);
            values['longitude'] = roundNumberUpToPrecision(values['longitude'], 6);
        }

        if (action === 'Add') {
            createLocation(values);
        } else if (action === 'Update' && currentLocation) {
            // Compare relevant location values to see if anything was updated.
            if (_.isEqual(values, _.omit(currentLocation, ['id', 'created_at', 'updated_at', 'deleted_at']))) {
                showMessages(nothingToUpdate, messageType.error);
                return;
            }
            updateLocation({...values, locationID: currentLocation.id});
        }
    }

    const resetDrawer = React.useCallback(() => {
        form.resetFields();
        handleClose();
    }, [form, handleClose]);

    React.useEffect(() => {
        if (createSuccess) {
            showMessages('Location successfully created.', messageType.success);
            resetDrawer();
            createReset();
        } else if (isCreateError) {
            showMessages(((createError) as BaseError).data?.message, messageType.error);
            createReset();
        }
    }, [createError, createReset, createSuccess, handleClose, isCreateError, resetDrawer]);

    React.useEffect(() => {
        if (updateSuccess) {
            showMessages('Location successfully updated.', messageType.success);
            resetDrawer();
            updateReset();
        } else if (isUpdateError) {
            showMessages(((updateError) as BaseError).data?.message, messageType.error);
            updateReset();
        }
    }, [updateSuccess, isUpdateError, updateError, updateReset, resetDrawer]);

    React.useEffect(() => {
        // ForceRender prop on Drawer is needed to stop a useForm error, which causes an issue with setFieldValue,
        //  needing it to be conditionally called in this useEffect.
        if (action === 'Update' && currentLocation) {
            form.setFieldsValue({
                name: currentLocation.name,
                description: currentLocation.description,
                latitude: currentLocation.latitude,
                longitude: currentLocation.longitude,
                public_note: currentLocation.public_note,
                private_note: currentLocation.private_note,
            });
        }
    }, [currentLocation, action, form]);

    React.useEffect(() => {
        // Reset lat/long error messages when no longer required.
        if (latLongEmpty) {
            form.setFields([{name: 'longitude', errors: []}, {name: 'latitude', errors: []}]);
        }
    }, [form, latLongEmpty]);

    return (
        <Drawer
            title={`${action === 'Update' ? 'Edit' : 'Add'} Location`}
            className="location-manage-drawer-wrapper"
            width={drawerWidth}
            open={drawerOpen}
            onClose={resetDrawer}
            forceRender={true}
        >
            <Form
                name="location_manage_drawer"
                layout="vertical"
                form={form}
                onFinish={handleFinish}
            >
                <Row className="full-width-row">
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{
                            type: 'string',
                            required: true,
                            message: 'Please enter a location name.'
                        }]}
                    >
                        <Input placeholder="Type here" name="name"/>
                    </Form.Item>
                </Row>
                <Row className="full-width-row">
                    <Form.Item
                        name="description"
                        label="Short Description"
                        rules={[{type: 'string', max: 255}]}
                    >
                        <Input placeholder="Type here" name="description"/>
                    </Form.Item>
                </Row>
                <Row className="full-width-row">
                    <Form.Item
                        name="latitude"
                        label="Latitude"
                        rules={[{type: 'number', required: !latLongEmpty, message: 'Please enter a latitude.'}]}
                    >
                        <InputNumber
                            name="latitude"
                            className="long-lat-full-width"
                            placeholder="Type here"
                            type="number"
                            min={-90}
                            max={90}
                        />
                    </Form.Item>
                </Row>
                <Row className="full-width-row">
                    <Form.Item
                        name="longitude"
                        label="Longitude"
                        rules={[{type: 'number', required: !latLongEmpty, message: 'Please enter a longitude.'}]}
                    >
                        <InputNumber
                            name="longitude"
                            className="long-lat-full-width"
                            placeholder="Type here"
                            type="number"
                            min={-180}
                            max={180}
                        />
                    </Form.Item>
                </Row>
                <Row className="full-width-row">
                    <Form.Item
                        name="public_note"
                        rules={[{type: 'string'}]}
                        label={
                            <div>
                                Parking Note
                                <Tooltip
                                    title="For any public notes regarding the location and is visible to all members (eg. parking notes, instructions on finding the location)."
                                >
                                    <InfoCircleOutlined className="title-icon"/>
                                </Tooltip>
                            </div>
                        }
                    >
                        <Input.TextArea
                            name="public_note"
                            placeholder="Type here"
                            autoSize={{minRows: 3}}
                        />
                    </Form.Item>
                </Row>
                <Row className="full-width-row">
                    <Form.Item
                        name="private_note"
                        rules={[{type: 'string'}]}
                        label={
                            <div>
                                Staff Location Note
                                <Tooltip
                                    title="For any private staff notes regarding the location that can be helpful to members when required (eg. where to park if a spot is taken). This is only visible to staff members."
                                >
                                    <InfoCircleOutlined className="title-icon"/>
                                </Tooltip>
                            </div>
                        }
                    >
                        <Input.TextArea
                            name="private_note"
                            placeholder="Type here"
                            autoSize={{minRows: 3}}
                        />
                    </Form.Item>
                </Row>
                <Row className="location-manage-drawer-buttons-row">
                    <Button
                        type="default"
                        onClick={resetDrawer}
                        className="location-manage-drawer-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={createLoading || updateLoading}
                    >
                        {action + ' Location'}
                    </Button>
                </Row>
            </Form>
        </Drawer>
    )
}

export default LocationManagementDrawer;
